import React, { useEffect, useState } from "react";
import { Button, Modal, Table, Toast, useToggleState } from "@yoast/ui-library";
import * as styles from "./TwoYearOffer.module.scss";
import { CheckCircleIcon, InformationCircleIcon } from "@heroicons/react/24/solid";
import { getUserId } from "shared-frontend/functions/auth";
import useRequest from "../../reactHooks/useRequest";
import ErrorDisplay from "../../errors/ErrorDisplay";
import ReactDOM from "react-dom";


/* eslint-disable react/prop-types */
// the jsdoc plugin can't handle ts imports

/**
 * Returns the modal block.
 *
 * @param {object} props The props object.
 * @param {import("../../../../nestjs-server/src/models/Subscription").Subscription} props.subscription The subscription object.
 * @param {boolean} props.isOpen Whether the modal is open.
 * @param {function} props.closeModal The function to close the modal.
 * @param {function} props.onConfirm The function to call when the confirm button is clicked.
 * @param {boolean} props.isLoading Whether the request is loading.
 * @param {object} props.error The error message, if any.
 *
 *
 * @returns {ReactElement} The modal block.
 */
export const ModalBlock = ( { subscription, isOpen, closeModal, onConfirm, isLoading, error } ) => {
	const errorDisplayError = error ? {
		message: "Something went wrong while processing your request. Please try again later.",
		type: "error",
	} : null;

	return (
		<Modal
			isOpen={ isOpen }
			onClose={ close }
		>
			<Modal.Panel>
				<Modal.CloseButton
					onClick={ closeModal }
					className="yst-bg-transparent yst-text-gray-500 focus:yst-ring-offset-0"
				/>
				<Modal.Title as="h2" size="2" className={ styles.modalTitle }>
					Confirm your 2-year renewal
				</Modal.Title>
				<div className="yst-space-y-4 yst-mb-4">
					<p>
						<strong>What&apos;s changing?</strong><br />
						You&apos;re switching to a <strong>2-year plan</strong> and <strong>saving 15%</strong> while
						you&apos;re at it. This kicks in on your next
						billing
						date: <strong>{ new Date( subscription.nextPayment || subscription.endDate )?.toLocaleDateString() }</strong>.
					</p>
					<p>
						<strong>What happens after?</strong><br />
						Once your 2 years are up, you&apos;ll return to yearly billing. No action needed unless you feel
						like mixing things up with more Yoast products.
					</p>
					<p>
						<strong>Changed your mind?</strong><br />
						You can update this setting anytime before renewal under <strong>Subscriptions &gt; Manage</strong>.
					</p>
					<hr className={ styles.hr } />
					<p className={ styles.small }>
						<em>
							<strong>Promotion valid on eligible products only.</strong> Exclusions apply. After the
							promotional period, all plans and products will renew at their current rate to the original
							annual term. Yoast may discontinue this promotion at any time.
						</em>
					</p>
				</div>
				<ErrorDisplay className="yst-mb-3" error={ errorDisplayError } />
				<div className={ styles.buttonContainer }>
					<Button variant="secondary" onClick={ closeModal }>
						Cancel
					</Button>
					<Button variant="primary" onClick={ onConfirm } isLoading={ isLoading }>
						Confirm renewal
					</Button>
				</div>
			</Modal.Panel>
		</Modal>
	);
};

/**
 * A toast that confirms the 2-year renewal.
 *
 * @param {object} props The props object.
 * @param {import("../../../../nestjs-server/src/models/Subscription").Subscription} props.subscription The subscription object.
 * @param {boolean} props.show Whether the toast is shown.
 * @param {function} props.onUndo The function to call when the undo button is clicked.
 * @param {boolean} props.isUndoing Whether the undo request is loading.
 *
 * @returns {Element} The toast element.
 */
const ConfirmToast = ( { subscription, show, onUndo, isUndoing } ) => {
	const [ isVisible, setIsVisible ] = useState( false );
	if ( ! show ) {
		return null;
	}
	return (
		<Toast
			id="two-year-offer-confirmed-toast"
			isVisible={ isVisible }
			setIsVisible={ setIsVisible }
			autoDismiss={ 15000 }
		>
			<div className={ styles.toastFlex }>
				<CheckCircleIcon className={ styles.checkIcon } />
				<div>
					<div className={ styles.toastClose }>
						<Toast.Close dismissScreenReaderLabel="Dismiss" />
					</div>
					<p>
						<strong>2-year renewal confirmed</strong>
					</p>
					<p>
						Your subscription (#{ subscription.subscriptionNumber }) will renew for 2 years
						starting <strong>{ new Date( subscription.nextPayment || subscription.endDate )?.toLocaleDateString() }</strong>.
					</p>
					<div className={ styles.buttonPosition }>
						<Button
							size="small"
							variant="tertiary"
							className={ styles.undoButton }
							onClick={ onUndo }
							isLoading={ isUndoing }
						>
							Undo
						</Button>
					</div>
				</div>

			</div>
		</Toast>
	);
};

/**
 * A toast that confirms the undoing of the 2-year renewal.
 *
 * @param {object} props The props object.
 * @param {import("../../../../nestjs-server/src/models/Subscription").Subscription} props.subscription The subscription object.
 * @param {boolean} props.show Whether the toast is shown.
 *
 * @returns {Element} The toast element.
 */
const UndoneToast = ( { subscription, show } ) => {
	const [ isVisible, setIsVisible ] = useState( false );
	if ( ! show ) {
		return null;
	}
	return (
		<Toast
			id="two-year-offer-undone-toast"
			isVisible={ isVisible }
			setIsVisible={ setIsVisible }
			autoDismiss={ 15000 }
		>
			<div className={ styles.toastFlex }>
				<InformationCircleIcon className={ styles.infoIcon } />
				<div>
					<div className={ styles.toastClose }>
						<Toast.Close dismissScreenReaderLabel="Dismiss" />
					</div>
					<p>
						<strong>2-year renewal undone</strong>
					</p>
					<p>
						Your subscription (#{ subscription.subscriptionNumber }) will renew yearly
						starting <strong>{ new Date( subscription.nextPayment || subscription.endDate )?.toLocaleDateString() }</strong>.
					</p>
				</div>

			</div>
		</Toast>
	);
};

const ErrorToast = ( { error } ) => {
	const [ isVisible, setIsVisible ] = useState( false );
	if ( ! error ) {
		return null;
	}
	return (
		<Toast
			id="two-year-offer-error-toast"
			isVisible={ isVisible }
			setIsVisible={ setIsVisible }
			autoDismiss={ 5000 }
		>
			<div className={ styles.toastFlex }>
				<ErrorDisplay
					error={ {
						message: "Something went wrong while processing your request. Please try again later.",
						type: "error",
					} }
				/>
			</div>
		</Toast>
	);
};

const TOAST_PORTAL_ID = "toast-root";

function ToastPortal( { children } ) {
	if ( typeof window === "undefined" ) {
		return null;
	}
	const toastRoot = document.getElementById( TOAST_PORTAL_ID );
	return toastRoot ? ReactDOM.createPortal( children, toastRoot ) : null;
}

/**
 *
 * @param {object} props The props object.
 * @param {import("../../../../nestjs-server/src/models/Subscription").Subscription[]} props.subscriptions
 * @param {boolean} props.isHidden Whether the subscription row is hidden.
 * @param {function} props.onHide The function to call when the subscription row should be hidden.
 * @param {function} props.onUnhide The function to call when the subscription row should be unhidden.
 * @returns {Element}
 */
export const SubscriptionRow = ( { subscriptions, isHidden, onHide, onUnhide } ) => {
	const subscription = subscriptions[ 0 ];
	const userId       = getUserId();

	const [ modalIsOpen, , , openModal, closeModal ]                      = useToggleState( false );
	const [ toastConfirmIsShown, , , showConfirmToast, hideConfirmToast ] = useToggleState( false );
	const [ toastUndoneIsShown, , , showUndoneToast, hideUndoneToast ]    = useToggleState( false );

	const switchToBiennialRequest     = useRequest(
		`Customers/${ userId }/subscriptions/${ subscription.id }/enable-biennial-billing`,
		"POST",
		{},
		{},
		false,
	);
	const undoSwitchToBiennialRequest = useRequest(
		`Customers/${ userId }/subscriptions/${ subscription.id }/disable-biennial-billing`,
		"POST",
		{},
		{},
		false,
	);

	const makeBiennial = switchToBiennialRequest.reload;
	const undoBiennial = undoSwitchToBiennialRequest.reload;

	useEffect( () => {
		if ( switchToBiennialRequest.isLoading ) {
			return;
		}
		if ( switchToBiennialRequest.data?.success ) {
			onHide( subscription.subscriptionNumber );
			closeModal();
			showConfirmToast();
			hideUndoneToast();
			switchToBiennialRequest.reset();
		}
	}, [ switchToBiennialRequest.data, switchToBiennialRequest.isLoading ] );


	useEffect( () => {
		if ( undoSwitchToBiennialRequest.isLoading ) {
			return;
		}
		if ( undoSwitchToBiennialRequest.data?.success ) {
			onUnhide( subscription.subscriptionNumber );
			showUndoneToast();
			hideConfirmToast();
			undoSwitchToBiennialRequest.reset();
		}
	}, [ undoSwitchToBiennialRequest.data, undoSwitchToBiennialRequest.isLoading ] );


	const modalsAndToasts = <>
		<ModalBlock
			subscription={ subscription }
			closeModal={ closeModal }
			isOpen={ modalIsOpen }
			onConfirm={ makeBiennial }
			error={ switchToBiennialRequest.error }
			isLoading={ switchToBiennialRequest.isLoading }
		/>
		<ToastPortal>
			<div className={ styles.toastContainer }>
				<ConfirmToast
					subscription={ subscription }
					show={ toastConfirmIsShown }
					onUndo={ undoBiennial }
					isLoading={ undoSwitchToBiennialRequest.isLoading }
				/>
				<UndoneToast
					subscription={ subscription }
					show={ toastUndoneIsShown }
				/>
				<ErrorToast error={ undoSwitchToBiennialRequest.error } />
			</div>
		</ToastPortal>
	</>;


	if ( isHidden ) {
		return modalsAndToasts;
	}

	return (
		<Table.Row>
			<Table.Cell>
				{ subscription.subscriptionNumber }
			</Table.Cell>
			<Table.Cell>
				<ul>
					{ subscriptions.map( ( sub ) => <li key={ sub.id + "-name" }>
						{ sub.limit } &times; { sub.name }
					</li> ) }
				</ul>
			</Table.Cell>
			<Table.Cell>
				{ new Date( subscription.nextPayment || subscription.endDate )?.toLocaleDateString() }
			</Table.Cell>
			<Table.Cell>
				{ Math.max( subscription.recurrenceRule?.count || 1, 1 ) } { subscription.recurrenceRule?.interval || "year" }
			</Table.Cell>
			<Table.Cell>
				{ modalsAndToasts }
				<Button onClick={ openModal } variant="upsell">
					Get 2-year renewal (15% off)
				</Button>
			</Table.Cell>
		</Table.Row>
	);
};
